import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Grid, Link, MenuItem, Paper, TextField, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import { useTranslation } from 'react-i18next';

import App from '../../../components/app/app';
import ErrorMessage from '../../../components/error-message';
import { AutoFill } from '../../../components/field-autofill';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { actions, selectors, types } from '../../../redux/reducers/rides-import/module';
import { actions as errorActions, selectors as errorSelectors } from '../../../redux/reducers/errors';
import CoreAPIService, {
    getGeoAutocompleteURL,
    getListCompanyGroupsURL,
    getListContractsURL,
    getListDriversURL,
    getListGroupUsersURL,
} from '../../../utils/core-api';
import envConfig from '../../../config';
import { RIDES_IMPORT_MAX_PRICE, RIDES_IMPORT_SIX_MONTHS_MS } from '../../../utils/constants';

interface SelectedEntity {
    id: string;
    name: string;
}

interface SelectedLocation {
    address: string;
    lat: number;
    lng: number;
}

interface DispatchGroupOption {
    id: string;
    name: string;
}

interface CostCenterOption {
    id: string;
    name: string;
}

interface FormState {
    plannedStart: Date | null;
    contractId: string;
    groupId: string;
    rider: SelectedEntity | null;
    booker: SelectedEntity | null;
    driver: SelectedEntity | null;
    serviceId: string;
    pickup: SelectedLocation | null;
    dropoff: SelectedLocation | null;
    priceBase: 'client' | 'driver';
    priceAmountHt: string;
    importReason: string;
    driverNote: string;
    costCenterId: string;
    externalRef: string;
}

const INITIAL_FORM: FormState = {
    plannedStart: null,
    contractId: '',
    groupId: '',
    rider: null,
    booker: null,
    driver: null,
    serviceId: '',
    pickup: null,
    dropoff: null,
    priceBase: 'client',
    priceAmountHt: '',
    importReason: '',
    driverNote: '',
    costCenterId: '',
    externalRef: '',
};

const RidesImportPage: React.FC = () => {
    const { t } = useTranslation('ride_import');
    const dispatch = useDispatch();
    const estimate = useSelector(selectors.estimate);
    const submitting = useSelector(selectors.submitting);
    const submitResult = useSelector(selectors.submitResult);
    const importRideError = useSelector(errorSelectors.importRideError);

    const [form, setForm] = useState<FormState>(INITIAL_FORM);
    const [services, setServices] = useState<DispatchGroupOption[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenterOption[]>([]);
    const [policiesLoading, setPoliciesLoading] = useState(false);

    const set = (key: keyof FormState) => (e: any) => {
        const value = e?.target?.value;
        if (value !== undefined) {
            setForm((f) => ({ ...f, [key]: value }));
        }
    };

    // Fetch policies (services + cost centers) when group changes
    useEffect(() => {
        if (!form.groupId) {
            setServices([]);
            setCostCenters([]);
            return;
        }
        setPoliciesLoading(true);
        CoreAPIService.get(
            `policies?groups[]=${form.groupId}&enrich_dispatch_groups=1`,
            null,
            null,
            envConfig.privapi_url,
        )
            .then((res: any) => {
                console.log('Policies response', res);
                const policy = res.payload.group[0];
                const dgs = policy?.dispatch_groups ?? policy?.dispatchGroups ?? [];
                setServices(
                    dgs.map((dg: any) => ({
                        id: String(dg.uuid || dg.id),
                        name: dg.name || dg.display_name || '',
                    })),
                );
                const ccs = policy?.cost_centers ?? policy?.costCenters ?? [];
                setCostCenters(
                    ccs.map((cc: any) => ({
                        id: String(cc.uuid || cc.id),
                        name: cc.name || '',
                    })),
                );
            })
            .catch(() => {
                setServices([]);
                setCostCenters([]);
            })
            .finally(() => setPoliciesLoading(false));
    }, [form.groupId]);

    const amountNumber = Number(form.priceAmountHt);
    const amountError = useMemo(() => {
        if (form.priceAmountHt === '') return null;
        if (Number.isNaN(amountNumber)) return t('errors.amount_nan');
        if (amountNumber < 0) return t('errors.amount_negative');
        if (amountNumber > RIDES_IMPORT_MAX_PRICE) return t('errors.amount_over_max', { max: RIDES_IMPORT_MAX_PRICE });
        return null;
    }, [form.priceAmountHt, amountNumber, t]);

    const dateError = useMemo(() => {
        if (!form.plannedStart) return null;
        const ts = form.plannedStart.getTime();
        if (ts >= Date.now()) return t('errors.date_future');
        if (Date.now() - ts > RIDES_IMPORT_SIX_MONTHS_MS) return t('errors.date_too_old');
        return null;
    }, [form.plannedStart, t]);

    const canSubmit =
        !submitting &&
        !amountError &&
        !dateError &&
        !!form.plannedStart &&
        !!form.groupId &&
        !!form.rider &&
        !!form.booker &&
        !!form.driver &&
        !!form.serviceId &&
        !!form.pickup &&
        !!form.dropoff &&
        form.priceAmountHt !== '' &&
        !!form.importReason;

    console.log('Form state', form, { amountError, dateError, canSubmit });

    const onEstimate = useCallback(() => {
        if (!form.pickup || !form.dropoff || !form.serviceId || !form.groupId) return;
        dispatch(
            actions.getPriceEstimate({
                company_id: form.groupId,
                service_id: form.serviceId,
                start_location: { lat: form.pickup.lat, lng: form.pickup.lng },
                end_location: { lat: form.dropoff.lat, lng: form.dropoff.lng },
            }) as any,
        );
    }, [form.pickup, form.dropoff, form.serviceId, form.groupId, dispatch]);

    const onSubmit = useCallback(() => {
        if (!form.rider || !form.booker || !form.driver || !form.pickup || !form.dropoff || !form.plannedStart) return;

        // Errors pile up and the banner shows the first match: drop the last failure so a new one replaces it
        dispatch(errorActions.clearError(types.IMPORT_RIDE));

        const source: 'manual' | 'auto' =
            estimate?.status === 'resolved' && estimate.amount_ht === amountNumber ? 'auto' : 'manual';

        dispatch(
            actions.importRide({
                planned_start_date: Math.floor(form.plannedStart.getTime() / 1000),
                pickup_address: form.pickup,
                dropoff_address: form.dropoff,
                rider_id: form.rider.id,
                booker_id: form.booker.id,
                driver_id: form.driver.id,
                group_id: form.groupId,
                service_id: form.serviceId,
                price: { source, base: form.priceBase, amount_ht: amountNumber },
                import_reason: form.importReason,
                booking_platform: `BackOffice ${process.env.REACT_APP_VERSION || ''}`.trim(),
                driver_note: form.driverNote || undefined,
                cost_center_id: form.costCenterId || undefined,
                external_ref: form.externalRef || undefined,
            }) as any,
        );
    }, [form, estimate, amountNumber, dispatch]);

    return (
        <App needRights={{ name: AccountRight.RIDE_ACTION_IMPORT, mode: RightRank.W }}>
            <Box p={2}>
                <Typography variant="h5" gutterBottom>
                    {t('title')}
                </Typography>

                <ErrorMessage payload={importRideError} />
                {submitResult && (
                    <Paper style={{ padding: 16, marginBottom: 16, background: '#e8f5e9' }}>
                        <Typography>{t('success')}</Typography>
                    </Paper>
                )}

                <Grid container spacing={2}>
                    {/* Date */}
                    <Grid item xs={12} md={6}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
                            <DateTimePicker
                                label={t('fields.planned_start')}
                                value={form.plannedStart}
                                onChange={(d) => setForm((f) => ({ ...f, plannedStart: d as Date }))}
                                format="dd/MM/yyyy HH:mm"
                                fullWidth
                                disableFuture
                                error={!!dateError}
                                helperText={dateError}
                                ampm={false}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>

                    {/* Company / Contract (autocomplete) */}
                    <Grid item xs={12} md={6}>
                        <AutoFill
                            formControl={false}
                            datas={{
                                id: 'id_contract',
                                name: 'contract',
                                label: t('fields.company'),
                                minChar: 1,
                                required: true,
                                variant: 'outlined',
                                isUuid: true,
                                onChange: (_idc: any, _text: any, item: any) => {
                                    const contractId = _idc || null;
                                    setForm((f) => ({
                                        ...f,
                                        contractId,
                                        groupId: '',
                                        rider: null,
                                        booker: null,
                                        serviceId: '',
                                        costCenterId: '',
                                    }));
                                },
                                api: getListContractsURL(),
                            }}
                        />
                    </Grid>

                    {/* Group (autocomplete, depends on contract) */}
                    <Grid item xs={12} md={6}>
                        {form.contractId ? (
                            <AutoFill
                                key={form.contractId}
                                formControl={false}
                                datas={{
                                    id: 'group',
                                    name: 'group',
                                    label: t('fields.group'),
                                    minChar: 0,
                                    required: true,
                                    variant: 'outlined',
                                    isUuid: true,
                                    onChange: (_idc: any, _text: any, item: any) => {
                                        const groupId = item ? String(item.uuid || item.id) : '';
                                        setForm((f) => ({
                                            ...f,
                                            groupId,
                                            rider: null,
                                            booker: null,
                                            serviceId: '',
                                            costCenterId: '',
                                        }));
                                    },
                                    api: getListCompanyGroupsURL(form.contractId),
                                }}
                            />
                        ) : (
                            <TextField
                                label={t('fields.group')}
                                disabled
                                fullWidth
                                placeholder={t('fields.select_company_first')}
                            />
                        )}
                    </Grid>

                    {/* Rider (autocomplete, depends on group) */}
                    <Grid item xs={12} md={6}>
                        {form.groupId ? (
                            <AutoFill
                                key={`rider-${form.groupId}`}
                                formControl={false}
                                datas={{
                                    id: 'rider',
                                    name: 'rider',
                                    label: t('fields.rider'),
                                    minChar: 1,
                                    required: true,
                                    variant: 'outlined',
                                    isUuid: true,
                                    onChange: (_idc: any, _text: any, item: any) => {
                                        setForm((f) => ({
                                            ...f,
                                            rider: item
                                                ? {
                                                      id: String(item.uuid || item.id),
                                                      name: item.fullname || item.name || '',
                                                  }
                                                : null,
                                        }));
                                    },
                                    api: getListGroupUsersURL(form.groupId),
                                    filterResult: (data: any[]) => data.filter((u) => u.revoked_at === null),
                                }}
                            />
                        ) : (
                            <TextField
                                label={t('fields.rider')}
                                disabled
                                fullWidth
                                placeholder={t('fields.select_group_first')}
                            />
                        )}
                    </Grid>

                    {/* Booker (autocomplete, depends on group) */}
                    <Grid item xs={12} md={6}>
                        {form.groupId ? (
                            <AutoFill
                                key={`booker-${form.groupId}`}
                                formControl={false}
                                datas={{
                                    id: 'booker',
                                    name: 'booker',
                                    label: t('fields.booker'),
                                    minChar: 1,
                                    required: true,
                                    variant: 'outlined',
                                    isUuid: true,
                                    onChange: (_idc: any, _text: any, item: any) => {
                                        setForm((f) => ({
                                            ...f,
                                            booker: item
                                                ? {
                                                      id: String(item.uuid || item.id),
                                                      name: item.fullname || item.name || '',
                                                  }
                                                : null,
                                        }));
                                    },
                                    api: getListGroupUsersURL(form.groupId),
                                    filterResult: (data: any[]) => data.filter((u) => u.revoked_at === null),
                                }}
                            />
                        ) : (
                            <TextField
                                label={t('fields.booker')}
                                disabled
                                fullWidth
                                placeholder={t('fields.select_group_first')}
                            />
                        )}
                    </Grid>

                    {/* Driver (autocomplete) */}
                    <Grid item xs={12} md={6}>
                        <AutoFill
                            formControl={false}
                            datas={{
                                id: 'driver',
                                name: 'driver',
                                label: t('fields.driver'),
                                minChar: 1,
                                required: true,
                                variant: 'outlined',
                                isUuid: true,
                                onChange: (_idc: any, _text: any, item: any) => {
                                    setForm((f) => ({
                                        ...f,
                                        driver: item
                                            ? {
                                                  id: String(item.uuid || item.id),
                                                  name: item.fullname || item.name || '',
                                              }
                                            : null,
                                    }));
                                },
                                onFind: (datas, id) => {
                                    return datas.find((x) => x.id == id);
                                },
                                api: getListDriversURL(),
                            }}
                        />
                    </Grid>

                    {/* Service / DispatchGroup (dropdown from policies) */}
                    <Grid item xs={12} md={6}>
                        {policiesLoading ? (
                            <Box display="flex" alignItems="center" style={{ gap: 8, height: 56 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : services.length > 0 ? (
                            <TextField
                                select
                                label={t('fields.service')}
                                value={form.serviceId}
                                onChange={set('serviceId') as any}
                                fullWidth
                                required
                                variant="outlined"
                            >
                                {services.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <TextField
                                label={t('fields.service')}
                                disabled
                                fullWidth
                                placeholder={t('fields.select_group_first')}
                            />
                        )}
                    </Grid>

                    {/* Cost center (dropdown from policies, optional) */}
                    <Grid item xs={12} md={6}>
                        {costCenters.length > 0 ? (
                            <TextField
                                select
                                label={t('fields.cost_center')}
                                value={form.costCenterId}
                                onChange={set('costCenterId') as any}
                                fullWidth
                                variant="outlined"
                            >
                                <MenuItem value="">{t('fields.none')}</MenuItem>
                                {costCenters.map((cc) => (
                                    <MenuItem key={cc.id} value={cc.id}>
                                        {cc.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <TextField
                                label={t('fields.cost_center')}
                                value={form.costCenterId}
                                onChange={set('costCenterId')}
                                fullWidth
                                placeholder="UUID"
                            />
                        )}
                    </Grid>

                    {/* Empty */}
                    {/* <Grid item xs={12} md={6} /> */}

                    {/* Pickup (location autocomplete) */}
                    <Grid item xs={12} md={6}>
                        <AutoFill
                            formControl={false}
                            datas={{
                                id: 'pickup',
                                name: 'pickup',
                                label: t('fields.pickup_address'),
                                location: true,
                                minChar: 3,
                                required: true,
                                variant: 'outlined',
                                style: {
                                    minWidth: 200,
                                    width: '100%',
                                    flex: 1,
                                },
                                onChange: (_idc: any, item: any) => {
                                    console.log('Selected pickup location', item);
                                    if (item?.location) {
                                        setForm((f) => ({
                                            ...f,
                                            pickup: {
                                                address: item.address?.text || '',
                                                lat: item.location[0] || item.location.lat,
                                                lng: item.location[1] || item.location.lng,
                                            },
                                        }));
                                    }
                                },
                                api: getGeoAutocompleteURL(),
                            }}
                        />
                    </Grid>

                    {/* Dropoff (location autocomplete) */}
                    <Grid item xs={12} md={6}>
                        <AutoFill
                            formControl={false}
                            datas={{
                                id: 'dropoff',
                                name: 'dropoff',
                                label: t('fields.dropoff_address'),
                                location: true,
                                minChar: 3,
                                required: true,
                                variant: 'outlined',
                                style: {
                                    minWidth: 200,
                                    width: '100%',
                                    flex: 1,
                                },
                                onChange: (_idc: any, item: any) => {
                                    if (item?.location) {
                                        setForm((f) => ({
                                            ...f,
                                            dropoff: {
                                                address: item.address?.text || '',
                                                lat: item.location[0] || item.location.lat,
                                                lng: item.location[1] || item.location.lng,
                                            },
                                        }));
                                    }
                                },
                                api: getGeoAutocompleteURL(),
                            }}
                        />
                    </Grid>

                    {/* Import reason */}
                    <Grid item xs={12}>
                        <TextField
                            label={t('fields.import_reason')}
                            value={form.importReason}
                            onChange={set('importReason')}
                            fullWidth
                            required
                            multiline
                            minRows={2}
                        />
                    </Grid>

                    {/* Price section */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" style={{ padding: 16, background: '#eef1ff' }}>
                            <Typography variant="caption" color="primary">
                                {t('estimator.title')}
                            </Typography>
                            <Box display="flex" alignItems="flex-start" style={{ gap: 8, marginTop: 8 }}>
                                <InfoIcon fontSize="small" />
                                <Typography variant="body2">{t('estimator.help')}</Typography>
                            </Box>
                            {!estimate && (
                                <Box mt={1}>
                                    <Link component="button" onClick={onEstimate}>
                                        {t('estimator.cta')}
                                    </Link>
                                </Box>
                            )}
                            {estimate?.status === 'error' && (
                                <Box mt={1}>
                                    <Typography variant="body2" color="error">
                                        {t('estimator.error')}
                                    </Typography>
                                    <Link component="button" onClick={onEstimate}>
                                        {t('estimator.cta')}
                                    </Link>
                                </Box>
                            )}
                            {estimate?.status === 'loading' && (
                                <Box mt={1}>
                                    <CircularProgress size={20} />
                                </Box>
                            )}
                            {estimate?.status === 'resolved' && (
                                <Box mt={1}>
                                    <Typography variant="body2">
                                        <strong>
                                            {t('estimator.resolved', { amount: estimate.amount_ht.toFixed(2) })}
                                        </strong>
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            setForm((f) => ({ ...f, priceAmountHt: String(estimate.amount_ht) }))
                                        }
                                        style={{ marginTop: 4 }}
                                    >
                                        {t('estimator.use_amount')}
                                    </Button>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={6} md={6}>
                        <TextField
                            select
                            label={t('fields.price_base')}
                            value={form.priceBase}
                            onChange={set('priceBase') as any}
                            fullWidth
                        >
                            <MenuItem value="client">{t('fields.price_base_client')}</MenuItem>
                            <MenuItem value="driver">{t('fields.price_base_driver')}</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <TextField
                            label={t('fields.price_amount_ht')}
                            value={form.priceAmountHt}
                            onChange={set('priceAmountHt')}
                            fullWidth
                            required
                            type="number"
                            error={!!amountError}
                            helperText={amountError}
                            inputProps={{ min: 0, max: RIDES_IMPORT_MAX_PRICE, step: 0.01 }}
                        />
                    </Grid>

                    {/* Optional fields */}
                    <Grid item xs={6} md={6}>
                        <TextField
                            label={t('fields.driver_note')}
                            value={form.driverNote}
                            onChange={set('driverNote')}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <TextField
                            label={t('fields.external_ref')}
                            value={form.externalRef}
                            onChange={set('externalRef')}
                            fullWidth
                        />
                    </Grid>

                    {/* Submit */}
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" disabled={!canSubmit} onClick={onSubmit}>
                            {submitting ? <CircularProgress size={20} /> : t('submit')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </App>
    );
};

export default RidesImportPage;
